import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { RefreshCw, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import { useSiteConfig } from "@/lib/siteConfig";
import ProductCard from "@/components/ProductCard";

const TABS = [
  { key: "used_refurbished", label: "Rivisionato", icon: Wrench, blurb: "Prodotti controllati, testati e sistemati dal nostro team." },
  { key: "used_asis", label: "NON Rivisionato", icon: RefreshCw, blurb: "Prodotti venduti così come sono, al prezzo più basso possibile." },
];

export default function UsedMarket() {
  const { config, loaded } = useSiteConfig();
  const um = config.used_market || {};
  const [tab, setTab] = useState("used_refurbished");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!um.enabled) return;
    setLoading(true);
    api.get("/products", { params: { condition: tab } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [tab, um.enabled]);

  if (!loaded) {
    return <div className="max-w-7xl mx-auto px-4 sm:px-8 py-24 text-center text-[var(--bs-muted)]">Caricamento…</div>;
  }
  if (!um.enabled) return <Navigate to="/" replace />;

  const active = TABS.find((t) => t.key === tab);
  const Icon = active.icon;

  return (
    <div>
      {/* Hero */}
      <section className="relative border-b border-[var(--bs-border)] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--bs-red)] mb-4 flex items-center gap-2">
              <span className="w-8 h-px bg-[var(--bs-red)]"></span> Occasioni uniche
            </div>
            <h1 data-testid="used-title" className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter mb-4">
              {um.title || "Mercatino dell'usato"}
            </h1>
            <p data-testid="used-subtitle" className="text-base sm:text-lg text-[var(--bs-muted)] max-w-xl leading-relaxed">
              {um.subtitle}
            </p>
          </div>
          {um.hero_image && (
            <div className="lg:col-span-5">
              <div className="aspect-[4/3] overflow-hidden border border-black">
                <img src={um.hero_image} alt="" className="w-full h-full object-cover"/>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
        <div className="flex flex-wrap gap-3 border-b border-[var(--bs-border)] mb-8">
          {TABS.map((t) => {
            const T = t.icon;
            const activeTab = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-widest border-b-2 -mb-px transition-colors ${activeTab ? "border-[var(--bs-red)] text-[var(--bs-red)]" : "border-transparent text-[var(--bs-muted)] hover:text-black"}`}
              >
                <T size={14}/> {t.label}
              </button>
            );
          })}
        </div>

        <div className="mb-6 flex items-center gap-3 text-sm">
          <Icon size={18} className="text-[var(--bs-red)]"/>
          <span className="text-[var(--bs-muted)]" data-testid="used-blurb">{active.blurb}</span>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => <div key={i} className="aspect-[3/4] bs-card bg-[var(--bs-surface)] animate-pulse"/>)}
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-[var(--bs-border)] p-16 text-center">
            <div className="font-display font-bold text-2xl mb-2">Nessun prodotto in questa sezione</div>
            <p className="text-[var(--bs-muted)] mb-6">Torna presto — carichiamo nuove occasioni ogni settimana.</p>
            <Link to="/shop" className="bs-btn-primary inline-flex px-6 py-3 text-xs font-bold uppercase tracking-widest">Vai al catalogo nuovo</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="used-grid">
            {items.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}
